import { httpClient } from './http-client';
import { ApiHelpers, SearchCriteriaBuilder } from '@/lib/search-criteria';
import type { ExtendedListParams, ListResponse } from '@/types/search-criteria';

/**
 * Базовый класс для всех API с поддержкой универсальных критериев поиска
 */
export abstract class BaseApi {
  /**
   * Строит query параметры из ExtendedListParams
   */
  static buildQueryParams(params?: ExtendedListParams): URLSearchParams {
    return ApiHelpers.buildQueryParams(params);
  }

  /**
   * Строит URL с query параметрами
   */
  static buildUrl(baseUrl: string, params?: ExtendedListParams): string {
    return ApiHelpers.buildUrl(baseUrl, params);
  }

  /**
   * Универсальный метод для получения списка с поддержкой критериев
   */
  static async getList<T>(endpoint: string, params?: ExtendedListParams): Promise<ListResponse<T>> {
    const url = this.buildUrl(endpoint, params);
    return httpClient.get<ListResponse<T>>(url);
  }

  /**
   * Универсальный метод для получения только общего количества записей
   */
  static async getTotalOnly<T>(endpoint: string, params?: Omit<ExtendedListParams, 'totalOnly'>): Promise<number> {
    const url = this.buildUrl(endpoint, { ...params, totalOnly: true });
    const response = await httpClient.get<ListResponse<T>>(url);
    return response.total || 0;
  }

  /**
   * Универсальный метод для получения элемента по ID
   */
  static async getById<T>(endpoint: string): Promise<T> {
    return httpClient.get<T>(endpoint);
  }

  /**
   * Универсальный метод для создания элемента
   */
  static async create<T, R>(endpoint: string, data: T): Promise<R> {
    return httpClient.post<R>(endpoint, data);
  }

  /**
   * Универсальный метод для обновления элемента
   */
  static async update<T, R>(endpoint: string, data: T): Promise<R> {
    return httpClient.put<R>(endpoint, data);
  }

  /**
   * Универсальный метод для удаления элемента
   */
  static async delete<T = void, D = any>(endpoint: string, data?: D): Promise<T> {
    return httpClient.delete<T, D>(endpoint, data);
  }

  /**
   * Создает билдер критериев поиска
   */
  static createCriteriaBuilder(mode: 'and' | 'or' = 'and'): SearchCriteriaBuilder {
    return SearchCriteriaBuilder.create(mode);
  }

  /**
   * Поиск по списку ID
   */
  static createIdSearchParams(ids: number[], additionalParams?: Omit<ExtendedListParams, 'criteria'>, idField: string = 'id'): ExtendedListParams {
    const criteria = SearchCriteriaBuilder
      .create('and')
      .addCondition(idField, 'eq', ids)
      .build();

    return {
      ...additionalParams,
      criteria
    };
  }

  /**
   * Поиск по тексту в указанных полях
   */
  static createTextSearchParams(searchText: string, additionalParams?: Omit<ExtendedListParams, 'criteria'>, fields: string[] = []): ExtendedListParams {
    const criteria = SearchCriteriaBuilder
      .create('or')
      .addGroup({
        conditions: fields.map(field => [field, 'ilike', `%${searchText}%`]),
        mode: 'or'
      })
      .build();

    return {
      ...additionalParams,
      criteria
    };
  }

  /**
   * Поиск только активных записей
   */
  static createActiveSearchParams(additionalParams?: Omit<ExtendedListParams, 'criteria'>, activeField: string = 'active'): ExtendedListParams {
    const criteria = SearchCriteriaBuilder
      .create('and')
      .addCondition(activeField, 'eq', true)
      .build();

    return {
      ...additionalParams,
      criteria
    };
  }

  /**
   * Поиск в диапазоне дат
   */
  static createDateRangeSearchParams(
    startDate: string, 
    endDate: string, 
    additionalParams?: Omit<ExtendedListParams, 'criteria'>,
    dateField: string = 'createdAt'
  ): ExtendedListParams {
    const criteria = SearchCriteriaBuilder
      .create('and')
      .addCondition(dateField, 'gte', startDate)
      .addCondition(dateField, 'lte', endDate)
      .build();

    return {
      ...additionalParams,
      criteria
    };
  }
}
